import { computed, inject, Signal, signal } from '@angular/core';
import { LogsService } from '@shared/services/logs';
import { isEmptyObject } from '@shared/utils';
import { BatchUpdatePayload } from '@stores/abstract/models';

/**
 * Abstract generic store base class for managing reactive state using Angular signals.
 * @template T Type representing the shape of the state object managed by the store.
 */
export abstract class StoreBase<T> {
  // Name used for logging identification. Should be overridden in subclasses.
  storeName: string = 'Store';
  protected readonly logs = inject(LogsService);
  private readonly store = signal<T>({} as T);
  read = this.store.asReadonly();

  /**
   * Updates the internal store state and logs the change with contextual store name.
   * Warns if the store name has not been overridden in subclasses.
   * @param data New state data of type T to set into the store.
   */
  set(data: T): void {
    this.store.set(data);
    if (this.storeName === 'Store') {
      this.logs.warn('💽 StoreBase', 'Store name is not defined', '\nState changed', data);
    } else {
      this.logs.info(`💽 ${this.storeName}`, 'State changed', Object.keys(data as object));
      this.logs.trace(`💽 ${this.storeName}`, 'State changed (full)', data);
    }
  }

  /**
   * Returns a computed signal selecting a specific property from the current state by key.
   * @param key The state property key to observe.
   * @returns A computed signal for the selected state property.
   */
  select<K extends keyof T>(key: K): Signal<NonNullable<T>[K] | undefined> {
    return computed(() => this.store()?.[key]);
  }

  /**
   * Returns a computed signal by applying a provided selector function onto the current state.
   * Useful for accessing deeply nested or computed properties.
   * @param selector Function to select desired properties or perform transformations on state.
   * @returns A computed signal of the selected or transformed state data.
   */
  selectPath<R>(selector: (state: T) => R): Signal<R | undefined> {
    return computed(() => {
      const state = this.store();
      return isEmptyObject(state) ? undefined : selector(state);
    });
  }

  /**
   * Updates a specific property of the state while preserving other properties.
   * @param key The property key to update
   * @param value The new value for the property
   */
  update<K extends keyof T>(key: K, value: T[K]): void {
    const currentState = this.store();
    if (currentState) {
      this.set({ ...currentState, [key]: value });
    }
  }

  /**
   * Deletes a key-value pair from the current store state.
   *
   * @param key The key of the property to be removed from the store.
   * @return {void} Does not return a value.
   */
  delete<K extends keyof T>(key: K): void {
    const currentState = this.store();
    if (currentState) {
      const newState = { ...currentState };
      delete newState[key];
      this.set(newState);
    }
  }

  /**
   * Updates the current state based on the provided payload, allowing batch updates and deletions.
   *
   * @param {BatchUpdatePayload<T>} payload - The payload containing the properties to update and keys to delete.
   * @param {Partial<T>} [payload.update] - An object containing properties to update in the current state.
   * @param {Array<string>} [payload.delete] - An array of keys to be removed from the current state.
   * @return {void} This method does not return a value.
   */
  batchUpdate(payload: BatchUpdatePayload<T>): void {
    const currentState = this.store();
    if (!currentState) return;

    let newState = { ...currentState };

    if (payload.update) {
      newState = { ...newState, ...payload.update };
    }

    if (payload.delete) {
      for (const key of payload.delete) {
        delete newState[key];
      }
    }
    this.set(newState);
  }
}
